import React from 'react';
import { StoreOffer, GownType, GownStyle, GownColor } from '../../api/StoreOffersAPI';
import { Star, Eye } from 'lucide-react';
import CONFIG from '../../config/config';

type Props = {
  offer: StoreOffer;
  onClick?: () => void;
};

const getGownTypeLabel = (type: GownType): string => {
  const labels: Record<GownType, string> = {
    'ballgown': 'Ballgown',
    'mermaid': 'Mermaid',
    'a-line': 'A-Line',
    'sheath': 'Sheath',
    'trumpet': 'Trumpet',
    'tea-length': 'Tea Length',
    'other': 'Other',
  };
  return labels[type] || type;
};

const getGownStyleLabel = (style: GownStyle): string => {
  const labels: Record<GownStyle, string> = {
    'classic': 'Classic',
    'modern': 'Modern',
    'vintage': 'Vintage',
    'boho': 'Boho',
    'romantic': 'Romantic',
    'minimalist': 'Minimalist',
  };
  return labels[style] || style;
};

const getGownColorLabel = (color: GownColor): string => {
  const labels: Record<GownColor, string> = {
    'white': 'White',
    'ivory': 'Ivory',
    'blush': 'Blush',
    'champagne': 'Champagne',
    'silver': 'Silver',
    'gold': 'Gold',
    'other': 'Other',
  };
  return labels[color] || color;
};

function StoreOfferCard({ offer, onClick }: Props) {
  const featuredImage = offer.featuredImage || offer.images[0];
  const currencySymbol = CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[offer.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] || offer.currency;

  return (
    <div
      onClick={onClick}
      className={`
        relative bg-white rounded-lg shadow-sm border border-gray-200
        overflow-hidden cursor-pointer transition-all duration-300
        hover:shadow-lg hover:scale-[1.02] hover:border-primary-300
        ${!offer.available ? 'opacity-60' : ''}
      `}
    >
      {/* Featured Badge */}
      {offer.featured && (
        <div className="absolute top-2 right-2 z-10 bg-yellow-400 text-yellow-900 rounded-full p-1.5 shadow-md">
          <Star className="h-4 w-4 fill-current" />
        </div>
      )}

      {/* Sale Badge */}
      {offer.onSale && (
        <div className="absolute top-2 left-2 z-10 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium shadow-md">
          On Sale
        </div>
      )}

      {/* Image */}
      <div className="relative w-full h-64 bg-gray-100 overflow-hidden">
        {featuredImage ? (
          <img
            src={featuredImage}
            alt={offer.name}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <Eye className="h-12 w-12 text-gray-400" />
          </div>
        )}

        {/* Availability Badge */}
        {!offer.available && !offer.onSale && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium z-10">
            Unavailable
          </div>
        )}

        {/* Image Count Badge */}
        {offer.images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs font-medium">
            {offer.images.length} images
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-lg text-gray-900 truncate">
          {offer.name}
        </h3>

        {offer.description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {offer.description}
          </p>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
            {getGownTypeLabel(offer.gownType)}
          </span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
            {getGownStyleLabel(offer.gownStyle)}
          </span>
          {offer.size && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
              Size: {offer.size}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex flex-col">
            {offer.onSale && offer.salePrice !== undefined ? (
              <>
                <span className="text-xl font-bold text-red-600">
                  {currencySymbol}{offer.salePrice.toLocaleString()}
                </span>
                <span className="text-sm text-gray-400 line-through">
                  {currencySymbol}{offer.price.toLocaleString()}
                </span>
              </>
            ) : (
              <span className="text-xl font-bold text-gray-900">
                {currencySymbol}{offer.price.toLocaleString()}
              </span>
            )}
          </div>
          {offer.gownColor && (
            <span className="text-xs text-gray-500">
              {getGownColorLabel(offer.gownColor)}
            </span>
          )}
        </div>
        
        {/* Quantity indicator */}
        {offer.available && offer.quantity !== undefined && (
          <div className="text-xs text-gray-500 mt-1">
            {offer.quantity > 0 
              ? `${offer.quantity} in stock`
              : 'Out of stock'
            }
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreOfferCard;

